import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tab,
  Tabs,
  ThemeProvider,
  createTheme,
  Paper,
  Fade,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Tooltip,
  Checkbox,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { estadisticasAPI } from '../services/api';
import './Innovation.css';

const ESTADO_DISPLAY = {
  oportunidad_identificada: 'Identificada',
  en_preparacion_propuesta: 'En preparación',
  supervision: 'Supervisión',
  entregada: 'Entregada',
  aprobado: 'Aprobado',
  planificada: 'Planificada',
  en_curso: 'En Curso',
  bloqueada: 'Bloqueada',
  finalizada: 'Finalizada',
  cancelada: 'Cancelada',
};

const getEstadoDisplay = (estado) => ESTADO_DISPLAY[estado] || estado;

const theme = createTheme({
  palette: {
    primary: { main: '#0d6efd' },
    secondary: { main: '#6c757d' },
    success: { main: '#198754' },
  },
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '1rem',
          fontWeight: 600,
          minWidth: 120,
          '&.Mui-selected': { color: '#0d6efd' },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: '#0d6efd', height: 3 },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        columnHeaders: {
          backgroundColor: 'white !important',
          color: 'black !important',
          fontWeight: 'bold !important',
          borderBottom: '2px solid #dee2e6 !important',
          '& .MuiDataGrid-columnHeaderTitle': { color: 'black !important', fontWeight: 'bold !important' },
          '& .MuiDataGrid-iconButtonContainer': { color: 'black !important' },
          '& .MuiDataGrid-menuIcon': { color: 'black !important' },
          '& .MuiDataGrid-sortIcon': { color: 'black !important' },
          '& .MuiDataGrid-filterIcon': { color: 'black !important' },
          '& .MuiDataGrid-columnSeparator': { color: '#dee2e6 !important' },
        },
        columnHeader: {
          backgroundColor: 'white !important',
          color: 'black !important',
          '& .MuiDataGrid-columnHeaderTitle': { color: 'black !important', fontWeight: 'bold !important' },
          '& .MuiDataGrid-iconButtonContainer': { color: 'black !important' },
          '& .MuiDataGrid-menuIcon': { color: 'black !important' },
          '& .MuiDataGrid-sortIcon': { color: 'black !important' },
          '& .MuiDataGrid-filterIcon': { color: 'black !important' },
        },
        columnHeaderTitle: { color: 'black !important', fontWeight: 'bold !important' },
      },
    },
  },
});

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`innovation-tabpanel-${index}`}
    aria-labelledby={`innovation-tab-${index}`}
    {...other}
  >
    {value === index && (
      <Fade in timeout={300}>
        <Box>{children}</Box>
      </Fade>
    )}
  </div>
);

const Innovation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fullIniciativas, setFullIniciativas] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  // Filtros locales
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState([]);
  const [estadosFiltro, setEstadosFiltro] = useState([]);
  const [areasFiltro, setAreasFiltro] = useState([]);
  const [distribTipoFiltro, setDistribTipoFiltro] = useState(null); // null = todos, 'innocard' | 'innotrans'
  const currentYear = new Date().getFullYear();
  const [añosSeleccionados, setAñosSeleccionados] = useState([]); // [] = todos los años

  // Leer filtros iniciales desde URL query params (e.g. venimos desde Home)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tipo = params.get('tipo');
    const estados = params.get('estados');
    if (tipo) setTipoFiltro([tipo]);
    if (estados) setEstadosFiltro([estados]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatNumber = (num) => {
    const n = Math.round(num ?? 0);
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return '—';
    const formatted = Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${formatted} €`;
  };

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsRes, listRes] = await Promise.all([
        estadisticasAPI.getGenerales(),
        estadisticasAPI.getInnovacionIniciativas({ historico: true }),
      ]);
      setStats(statsRes.data);
      setFullIniciativas(listRes.data || []);
    } catch (err) {
      setError('Error al cargar las estadísticas de innovación');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Valores únicos para selectores
  const areasUnicas = useMemo(() => {
    if (!fullIniciativas.length) return [];
    return [...new Set(fullIniciativas.map((i) => i.nombre_de_la_area).filter(Boolean))].sort();
  }, [fullIniciativas]);

  const estadosUnicos = useMemo(() => {
    if (!fullIniciativas.length) return [];
    return [...new Set(fullIniciativas.map((i) => i.estado).filter(Boolean))].sort();
  }, [fullIniciativas]);

  const añosDisponibles = useMemo(() => {
    if (!fullIniciativas.length) {
      const years = [];
      for (let i = 0; i <= 3; i++) years.push(currentYear - i);
      return years;
    }
    const yearsSet = new Set();
    fullIniciativas.forEach(i => { if (i.año) yearsSet.add(i.año); });
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [fullIniciativas, currentYear]);

  // Iniciativas filtradas
  const iniciativasFiltradas = useMemo(() => {
    return fullIniciativas.filter((ini) => {
      if (añosSeleccionados.length > 0 && !añosSeleccionados.includes(ini.año)) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        if (
          !(ini.nombre_de_la_oportunidad || '').toLowerCase().includes(q) &&
          !(ini.nombre_de_la_area || '').toLowerCase().includes(q) &&
          !(ini.negocio || '').toLowerCase().includes(q)
        ) return false;
      }
      if (tipoFiltro.length > 0 && !tipoFiltro.includes(ini.tipo_innovacion)) return false;
      if (estadosFiltro.length > 0 && !estadosFiltro.includes(ini.estado)) return false;
      if (areasFiltro.length > 0 && !areasFiltro.includes(ini.nombre_de_la_area)) return false;
      return true;
    });
  }, [fullIniciativas, searchTerm, tipoFiltro, estadosFiltro, areasFiltro, añosSeleccionados]);

  // Helpers para selectores múltiples (mismo patrón que Operaciones)
  const isAllSelected = (selected, all) =>
    selected.length === 0 || (all.length > 0 && selected.length === all.length);

  const handleSelectChange = (event, allItems, setter, current) => {
    const value = event.target.value;
    if (value[value.length - 1] === 'all') {
      setter(isAllSelected(current, allItems) ? [] : allItems);
      return;
    }
    setter(value);
  };

  const getRenderValue = (selected, all, label) => {
    if (selected.length === 0 || isAllSelected(selected, all)) return 'Todos';
    if (selected.length === 1) return selected[0] === 'innocard' ? 'Innocard' : selected[0] === 'innotrans' ? 'Innotrans' : getEstadoDisplay(selected[0]) || selected[0];
    return `${selected.length} seleccionados`;
  };

  const limpiarFiltros = () => {
    setSearchTerm('');
    setTipoFiltro([]);
    setEstadosFiltro([]);
    setAreasFiltro([]);
    setAñosSeleccionados([]);
  };

  // Refs y estado para drag-scroll del DataGrid (mismo patrón que TablaOperaciones / TablaDesarrolloNegocio)
  const scrollRef = useRef(null);
  const gridRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (!scrollRef.current) return;
      setIsDragging(true);
      setStartX(e.pageX - scrollRef.current.offsetLeft);
      setScrollLeft(scrollRef.current.scrollLeft);
      scrollRef.current.style.cursor = 'grabbing';
      scrollRef.current.style.userSelect = 'none';
    };
    const handleMouseLeave = () => {
      if (!scrollRef.current) return;
      setIsDragging(false);
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = 'auto';
    };
    const handleMouseUp = () => {
      if (!scrollRef.current) return;
      setIsDragging(false);
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = 'auto';
    };
    const handleMouseMove = (e) => {
      if (!isDragging || !scrollRef.current) return;
      e.preventDefault();
      const x = e.pageX - scrollRef.current.offsetLeft;
      const walk = (x - startX) * 2;
      scrollRef.current.scrollLeft = scrollLeft - walk;
    };
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('mousedown', handleMouseDown);
      el.addEventListener('mouseleave', handleMouseLeave);
      el.addEventListener('mouseup', handleMouseUp);
      el.addEventListener('mousemove', handleMouseMove);
      el.addEventListener('dragstart', (e) => e.preventDefault());
      return () => {
        el.removeEventListener('mousedown', handleMouseDown);
        el.removeEventListener('mouseleave', handleMouseLeave);
        el.removeEventListener('mouseup', handleMouseUp);
        el.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [isDragging, startX, scrollLeft]);

  // KPIs calculados desde fullIniciativas filtrado por año (reactivo al filtro de año)
  const kpisComputed = useMemo(() => {
    const base = añosSeleccionados.length === 0
      ? fullIniciativas
      : fullIniciativas.filter(i => añosSeleccionados.includes(i.año));

    const numTotal      = base.length;
    const numInnocards  = base.filter(i => i.tipo_innovacion === 'innocard').length;
    const numInnotrans  = base.filter(i => i.tipo_innovacion === 'innotrans').length;
    const costEst       = base.reduce((s, i) => s + (parseFloat(i.coste_total_estimado)  || 0), 0);
    const costInc       = base.reduce((s, i) => s + (parseFloat(i.coste_total_incurrido) || 0), 0);
    const enCurso       = base.filter(i => i.estado === 'en_curso').length;
    const finalizada    = base.filter(i => i.estado === 'finalizada').length;
    const planificadas  = base.filter(i => ['planificada', 'en_curso', 'finalizada', 'bloqueada'].includes(i.estado)).length;

    return {
      num_innocards:           numInnocards,
      num_innotrans:           numInnotrans,
      coste_total_estimado:    costEst,
      coste_total_incurrido:   costInc,
      total:                   numTotal,
      pct_ejecutado:           costEst > 0 ? Math.round((costInc / costEst) * 100) : 0,
      ratio_tareas_en_curso:   numTotal > 0 ? (enCurso    / numTotal) * 100 : 0,
      ratio_tareas_finalizadas:numTotal > 0 ? (finalizada / numTotal) * 100 : 0,
      tasa_conversion:         numTotal > 0 ? (planificadas / numTotal) * 100 : 0,
      ratio_finalizacion:      planificadas > 0 ? (finalizada / planificadas) * 100 : 0,
    };
  }, [fullIniciativas, añosSeleccionados]);

  const innovacion = {
    ...(stats?.innovacion || {}),
    num_innocards:        kpisComputed.num_innocards,
    num_innotrans:        kpisComputed.num_innotrans,
    coste_total_estimado: kpisComputed.coste_total_estimado,
    coste_total_incurrido:kpisComputed.coste_total_incurrido,
  };
  const kpis = {
    ...(stats?.innovacion?.kpis || {}),
    ratio_tareas_en_curso:    kpisComputed.ratio_tareas_en_curso,
    ratio_tareas_finalizadas: kpisComputed.ratio_tareas_finalizadas,
    tasa_conversion:          kpisComputed.tasa_conversion,
    ratio_finalizacion:       kpisComputed.ratio_finalizacion,
  };
  const total       = kpisComputed.total;
  const pctEjecutado = kpisComputed.pct_ejecutado;

  // Datos filtrados para la pestaña de Distribución
  const distribIniciativas = useMemo(() => {
    // Solo iniciativas con tipo conocido, para que el total cuadre con num_innocards + num_innotrans
    const withTipo = fullIniciativas.filter(
      i => (i.tipo_innovacion === 'innocard' || i.tipo_innovacion === 'innotrans') &&
           (añosSeleccionados.length === 0 || añosSeleccionados.includes(i.año))
    );
    if (!distribTipoFiltro) return withTipo;
    return withTipo.filter(i => i.tipo_innovacion === distribTipoFiltro);
  }, [fullIniciativas, distribTipoFiltro, añosSeleccionados]);

  const distribCostesEstimado = distribIniciativas.reduce((s, i) => s + (parseFloat(i.coste_total_estimado) || 0), 0);
  const distribCostesIncurrido = distribIniciativas.reduce((s, i) => s + (parseFloat(i.coste_total_incurrido) || 0), 0);
  const distribPctEjecutado = distribCostesEstimado > 0 ? Math.round((distribCostesIncurrido / distribCostesEstimado) * 100) : 0;

  // Columnas para el DataGrid (mismo patrón que TablaOperaciones / TablaDesarrolloNegocio)
  const columns = [
    {
      field: 'nombre_de_la_oportunidad',
      headerName: 'Iniciativa',
      flex: 1.5,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', '&:hover': { textDecoration: 'underline' } }}>
          <Link
            to={`/projects/${params.row.id}?from=innovacion`}
            style={{ textDecoration: 'none', color: '#0d6efd', fontWeight: 'bold' }}
          >
            {params.value || '-'}
          </Link>
        </Box>
      ),
    },
    {
      field: 'tipo_innovacion',
      headerName: 'Tipo',
      flex: 0.5,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value === 'innocard' ? 'Innocard' : 'Innotrans'}
          size="small"
          sx={{
            backgroundColor: params.value === 'innocard' ? '#e7f1ff' : '#fff3e0',
            color: params.value === 'innocard' ? '#0d6efd' : '#fd7e14',
            fontWeight: 600,
            fontSize: '0.7rem',
          }}
        />
      ),
    },
    {
      field: 'estado',
      headerName: 'Estado',
      flex: 0.7,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={getEstadoDisplay(params.value)}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
      ),
    },
    {
      field: 'nombre_de_la_area',
      headerName: 'Área de conocimiento',
      flex: 0.9,
      minWidth: 160,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'negocio',
      headerName: 'Negocio',
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'coste_total_estimado',
      headerName: 'Coste Estimado',
      flex: 0.7,
      minWidth: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 500, fontSize: '0.875rem', color: '#212529' }}>
          {formatCurrency(Number(params.value))}
        </Typography>
      ),
    },
    {
      field: 'coste_total_incurrido',
      headerName: 'Coste Incurrido',
      flex: 0.7,
      minWidth: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 500, fontSize: '0.875rem', color: '#212529' }}>
          {formatCurrency(Number(params.value))}
        </Typography>
      ),
    },
  ];

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ width: '100%', px: { xs: 1, sm: 2, md: 3 }, py: 2 }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ width: '100%', px: { xs: 1, sm: 2, md: 3 }, py: 2 }}>
          <Alert severity="error" action={<Button size="small" onClick={loadStats}>Reintentar</Button>}>
            {error}
          </Alert>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ width: '100%', maxWidth: '100vw', overflow: 'hidden', px: { xs: 1, sm: 2, md: 3 }, py: 2 }}>

        {/* Navegación por Tabs */}
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ pb: 0 }}>

            {/* ── KPIs principales ── */}
            <Box sx={{
              mb: 2,
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
              gap: 1.5
            }}>
              <Tooltip title="Número total de iniciativas de innovación (Innocards + Innotrans)" placement="top" arrow>
                <Paper sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'default' }}>
                  <Typography variant="h6" sx={{ fontSize: '1.4rem', fontWeight: 500, color: '#212529', mb: 0.5 }}>
                    {formatNumber(total)}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                    Total Iniciativas
                  </Typography>
                </Paper>
              </Tooltip>

              <Tooltip title="Iniciativas de tipo Innocard: ideas de innovación internas propuestas por los equipos" placement="top" arrow>
                <Paper sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'default' }}>
                  <Typography variant="h6" sx={{ fontSize: '1.4rem', fontWeight: 500, color: '#212529', mb: 0.5 }}>
                    {formatNumber(innovacion.num_innocards || 0)}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                    Innocards
                  </Typography>
                </Paper>
              </Tooltip>

              <Tooltip title="Iniciativas de tipo Innotrans: proyectos de transferencia tecnológica e innovación aplicada" placement="top" arrow>
                <Paper sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'default' }}>
                  <Typography variant="h6" sx={{ fontSize: '1.4rem', fontWeight: 500, color: '#212529', mb: 0.5 }}>
                    {formatNumber(innovacion.num_innotrans || 0)}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                    Innotrans
                  </Typography>
                </Paper>
              </Tooltip>

              <Tooltip title="Suma de todos los costes presupuestados: desarrollo, gastos, hardware, OPEX, alquiler de equipos, riesgos y estructura" placement="top" arrow>
                <Paper sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'default' }}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500, color: '#212529', mb: 0.5 }}>
                    {formatCurrency(innovacion.coste_total_estimado || 0)}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                    Coste Estimado
                  </Typography>
                </Paper>
              </Tooltip>

              <Tooltip title="Suma de todos los costes reales incurridos hasta la fecha: desarrollo, gastos, hardware, OPEX, alquiler, riesgos y estructura" placement="top" arrow>
                <Paper sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'default' }}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500, color: '#212529', mb: 0.5 }}>
                    {formatCurrency(innovacion.coste_total_incurrido || 0)}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                    Coste Incurrido
                  </Typography>
                </Paper>
              </Tooltip>

              <Tooltip title="Porcentaje del presupuesto ya ejecutado: Coste Incurrido / Coste Estimado × 100. Rojo si supera el 100%" placement="top" arrow>
                <Paper sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'default' }}>
                  <Typography variant="h6" sx={{ fontSize: '1.4rem', fontWeight: 500, color: pctEjecutado > 100 ? '#dc3545' : '#198754', mb: 0.5 }}>
                    {pctEjecutado}%
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                    % Ejecutado
                  </Typography>
                </Paper>
              </Tooltip>
            </Box>

            {/* ── KPIs de Rendimiento ── */}
            {Object.keys(kpis).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#495057', mb: 1, display: 'block' }}>
                  KPIs de Rendimiento y Gestión
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(8, 1fr)' },
                  gap: 1
                }}>
                  <Tooltip title="% de iniciativas en estado 'En Curso' respecto al total. Indica la carga de trabajo activa del equipo" placement="top" arrow>
                    <Paper sx={{ p: 1, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'default' }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500, color: '#212529', mb: 0.3 }}>
                        {kpis.ratio_tareas_en_curso !== undefined ? `${formatNumber(Math.round(kpis.ratio_tareas_en_curso))}%` : 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#6c757d', lineHeight: 1.2 }}>
                        Carga Activa
                      </Typography>
                    </Paper>
                  </Tooltip>

                  <Tooltip title="% de iniciativas en estado 'Finalizada' respecto al total. Mide la eficiencia de cierre del equipo" placement="top" arrow>
                    <Paper sx={{ p: 1, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'default' }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500, color: '#212529', mb: 0.3 }}>
                        {kpis.ratio_tareas_finalizadas !== undefined ? `${formatNumber(Math.round(kpis.ratio_tareas_finalizadas))}%` : 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#6c757d', lineHeight: 1.2 }}>
                        Productividad
                      </Typography>
                    </Paper>
                  </Tooltip>

                  <Tooltip title="% de oportunidades identificadas que pasan a estado 'Planificada'. Refleja la madurez del pipeline de innovación" placement="top" arrow>
                    <Paper sx={{ p: 1, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'default' }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500, color: '#212529', mb: 0.3 }}>
                        {kpis.tasa_conversion !== undefined ? `${formatNumber(Math.round(kpis.tasa_conversion))}%` : 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#6c757d', lineHeight: 1.2 }}>
                        Conversión
                      </Typography>
                    </Paper>
                  </Tooltip>

                  <Tooltip title="% de iniciativas planificadas que llegan a 'Finalizada'. Indica el ratio de éxito de la ejecución" placement="top" arrow>
                    <Paper sx={{ p: 1, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'default' }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500, color: '#212529', mb: 0.3 }}>
                        {kpis.ratio_finalizacion !== undefined ? `${formatNumber(Math.round(kpis.ratio_finalizacion))}%` : 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#6c757d', lineHeight: 1.2 }}>
                        Finalización
                      </Typography>
                    </Paper>
                  </Tooltip>

                  <Tooltip title="Días promedio desde que se identifica una oportunidad hasta que se finaliza la iniciativa" placement="top" arrow>
                    <Paper sx={{ p: 1, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'default' }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500, color: '#212529', mb: 0.3 }}>
                        {kpis.lead_time !== undefined ? `${formatNumber(Math.round(kpis.lead_time))}d` : 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#6c757d', lineHeight: 1.2 }}>
                        Lead Time
                      </Typography>
                    </Paper>
                  </Tooltip>

                  <Tooltip title="Días promedio desde que una iniciativa pasa a 'Planificada' hasta que se finaliza. Mide la eficiencia de ejecución" placement="top" arrow>
                    <Paper sx={{ p: 1, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'default' }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500, color: '#212529', mb: 0.3 }}>
                        {kpis.cycle_time !== undefined ? `${formatNumber(Math.round(kpis.cycle_time))}d` : 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#6c757d', lineHeight: 1.2 }}>
                        Cycle Time
                      </Typography>
                    </Paper>
                  </Tooltip>

                  <Tooltip title="Promedio de semanas de retraso respecto a la fecha planificada. Verde ≤4 sem · Amarillo ≤8 sem · Rojo >8 sem" placement="top" arrow>
                    <Paper sx={{ p: 1, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'default' }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500, color: kpis.desviacion_planificacion != null ? (kpis.desviacion_planificacion > 8 ? '#dc3545' : kpis.desviacion_planificacion > 4 ? '#e67e00' : '#198754') : '#212529', mb: 0.3 }}>
                        {kpis.desviacion_planificacion != null ? `${formatNumber(Math.round(kpis.desviacion_planificacion))} sem` : 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#6c757d', lineHeight: 1.2 }}>
                        Desviación
                      </Typography>
                    </Paper>
                  </Tooltip>

                  <Tooltip title="Días promedio entre el inicio real y la entrega de las iniciativas finalizadas" placement="top" arrow>
                    <Paper sx={{ p: 1, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'default' }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500, color: '#212529', mb: 0.3 }}>
                        {kpis.tiempo_promedio_ejecucion !== undefined ? `${formatNumber(Math.round(kpis.tiempo_promedio_ejecucion))}d` : 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#6c757d', lineHeight: 1.2 }}>
                        T. Ejecución
                      </Typography>
                    </Paper>
                  </Tooltip>
                </Box>
              </Box>
            )}

            {/* ── Filtros ── */}
            <Box sx={{ mt: '2rem', mb: '1rem', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>

              <TextField
                variant="outlined"
                placeholder="Buscar iniciativas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <i className="fas fa-search"></i>
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{ minWidth: 200, flex: 1, maxWidth: { xs: '100%', md: 300 } }}
              />

              {/* Año */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Año</InputLabel>
                <Select
                  multiple
                  value={añosSeleccionados}
                  label="Año"
                  onChange={(e) => handleSelectChange(e, añosDisponibles, setAñosSeleccionados, añosSeleccionados)}
                  renderValue={() => getRenderValue(añosSeleccionados, añosDisponibles, 'años')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(añosSeleccionados, añosDisponibles)}
                        indeterminate={añosSeleccionados.length > 0 && añosSeleccionados.length < añosDisponibles.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {añosDisponibles.map((año) => (
                    <MenuItem key={año} value={año} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={añosSeleccionados.length === 0 || añosSeleccionados.includes(año)} />
                      </ListItemIcon>
                      <ListItemText primary={año} />
                      {año === currentYear && (
                        <Chip label="Actual" size="small" sx={{ ml: 'auto', fontSize: '0.6rem', height: '16px' }} />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Tipo Innovación */}
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Tipo Innovación</InputLabel>
                <Select
                  multiple
                  value={tipoFiltro}
                  label="Tipo Innovación"
                  onChange={(e) => handleSelectChange(e, ['innocard', 'innotrans'], setTipoFiltro, tipoFiltro)}
                  renderValue={() => getRenderValue(tipoFiltro, ['innocard', 'innotrans'], 'tipos')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox checked={isAllSelected(tipoFiltro, ['innocard', 'innotrans'])} indeterminate={tipoFiltro.length > 0 && tipoFiltro.length < 2} />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  <MenuItem value="innocard" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox checked={tipoFiltro.length === 0 || tipoFiltro.includes('innocard')} />
                    </ListItemIcon>
                    <ListItemText primary="Innocard" />
                  </MenuItem>
                  <MenuItem value="innotrans" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox checked={tipoFiltro.length === 0 || tipoFiltro.includes('innotrans')} />
                    </ListItemIcon>
                    <ListItemText primary="Innotrans" />
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Estado */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Estado</InputLabel>
                <Select
                  multiple
                  value={estadosFiltro}
                  label="Estado"
                  onChange={(e) => handleSelectChange(e, estadosUnicos, setEstadosFiltro, estadosFiltro)}
                  renderValue={() => getRenderValue(estadosFiltro, estadosUnicos, 'estados')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox checked={isAllSelected(estadosFiltro, estadosUnicos)} indeterminate={estadosFiltro.length > 0 && estadosFiltro.length < estadosUnicos.length} />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {estadosUnicos.map((estado) => (
                    <MenuItem key={estado} value={estado} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={estadosFiltro.length === 0 || estadosFiltro.includes(estado)} />
                      </ListItemIcon>
                      <ListItemText primary={getEstadoDisplay(estado)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Área */}
              {areasUnicas.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Área conocimiento</InputLabel>
                  <Select
                    multiple
                    value={areasFiltro}
                    label="Área conocimiento"
                    onChange={(e) => handleSelectChange(e, areasUnicas, setAreasFiltro, areasFiltro)}
                    renderValue={() => getRenderValue(areasFiltro, areasUnicas, 'áreas')}
                    sx={{ fontSize: '0.85rem' }}
                    displayEmpty
                  >
                    <MenuItem value="all">
                      <ListItemIcon>
                        <Checkbox checked={isAllSelected(areasFiltro, areasUnicas)} indeterminate={areasFiltro.length > 0 && areasFiltro.length < areasUnicas.length} />
                      </ListItemIcon>
                      <ListItemText primary="Seleccionar todas" />
                    </MenuItem>
                    {areasUnicas.map((area) => (
                      <MenuItem key={area} value={area} sx={{ fontSize: '0.85rem' }}>
                        <ListItemIcon>
                          <Checkbox checked={areasFiltro.length === 0 || areasFiltro.includes(area)} />
                        </ListItemIcon>
                        <ListItemText primary={area} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Limpiar filtros */}
              <Button
                variant="contained"
                size="small"
                onClick={limpiarFiltros}
                sx={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  borderRadius: '6px',
                  boxShadow: '0 2px 4px rgba(220, 53, 69, 0.2)',
                  '&:hover': { backgroundColor: '#c82333', boxShadow: '0 4px 8px rgba(220, 53, 69, 0.3)', transform: 'translateY(-1px)' },
                  '&:active': { transform: 'translateY(0px)' },
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <i className="fa-solid fa-filter-circle-xmark" style={{ marginRight: '6px', fontSize: '0.9rem' }}></i>
                Limpiar filtros
              </Button>

              {/* Recargar */}
              <Button
                variant="contained"
                size="small"
                onClick={loadStats}
                disabled={loading}
                sx={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  borderRadius: '6px',
                  boxShadow: '0 2px 4px rgba(0, 123, 255, 0.2)',
                  '&:hover': { backgroundColor: '#0056b3', boxShadow: '0 4px 8px rgba(0, 123, 255, 0.3)', transform: 'translateY(-1px)' },
                  '&:active': { transform: 'translateY(0px)' },
                  '&:disabled': { backgroundColor: '#e9ecef', color: '#6c757d' },
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <i className={`fa-solid ${loading ? 'fa-spinner fa-spin' : 'fa-arrows-rotate'}`} style={{ marginRight: '6px', fontSize: '0.9rem' }}></i>
                {loading ? 'Cargando...' : 'Actualizar'}
              </Button>
            </Box>

            {/* ── Pestañas + botón nueva iniciativa ── */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{
                  '& .MuiTabs-flexContainer': { gap: 2 },
                  '& .MuiTabs-indicator': { bottom: 0 },
                  flex: 1,
                }}
              >
                <Tab
                  icon={<i className="fas fa-table"></i>}
                  iconPosition="start"
                  label={`Lista de iniciativas (${iniciativasFiltradas.length})`}
                  sx={{ minHeight: 60, gap: 1 }}
                />
                <Tab
                  icon={<i className="fas fa-chart-pie"></i>}
                  iconPosition="start"
                  label="Distribución"
                  sx={{ minHeight: 60, gap: 1 }}
                />
              </Tabs>

              <Button
                variant="outlined"
                size="small"
                sx={{
                  backgroundColor: '#212529',
                  color: '#fff',
                  borderColor: '#212529',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  px: 2,
                  py: 1,
                  textTransform: 'none',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  '&:hover': { backgroundColor: '#495057', borderColor: '#212529', color: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.25)', transform: 'translateY(-1px)' },
                  width: { xs: '100%', sm: 'auto' },
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease-in-out',
                  mb: { xs: 0, sm: 1 },
                }}
                onClick={() => navigate('/crear-iniciativa-innovacion')}
              >
                + Nueva iniciativa
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* ── Tab 0: Lista de iniciativas ── */}
        <TabPanel value={activeTab} index={0}>
          <Card sx={{
            width: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: { xs: 'calc(100vh - 300px)', sm: 'calc(100vh - 270px)', md: 'calc(100vh - 240px)' },
            boxShadow: 3,
            borderRadius: 2,
          }}>
            <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box
                sx={{ flex: 1, width: '100%', minHeight: 0 }}
                ref={scrollRef}
                className={`mui-datagrid-container ${isDragging ? 'dragging' : ''}`}
              >
                <DataGrid
                  ref={gridRef}
                  rows={iniciativasFiltradas}
                  columns={columns}
                  pinnedColumns={{ left: ['nombre_de_la_oportunidad'] }}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 25 } },
                    sorting: { sortModel: [{ field: 'nombre_de_la_oportunidad', sort: 'asc' }] },
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  checkboxSelection={false}
                  disableRowSelectionOnClick={true}
                  autoHeight={false}
                  disableColumnSelector={false}
                  disableColumnMenu={false}
                  disableColumnSorting={false}
                  sortingMode="client"
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                  onRowClick={undefined}
                  sx={{
                    border: 'none',
                    height: '100%',
                    width: '100%',
                    '& .MuiDataGrid-main': { overflow: 'auto' },
                    '& .MuiDataGrid-virtualScroller': { overflowX: 'auto', overflowY: 'auto' },
                    '& .MuiDataGrid-cell': {
                      cursor: 'default',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                    '& .MuiDataGrid-cell[data-field="nombre_de_la_oportunidad"]': {
                      justifyContent: 'flex-start',
                    },
                    '& .MuiDataGrid-cell[data-field="coste_total_estimado"]': { justifyContent: 'flex-end' },
                    '& .MuiDataGrid-cell[data-field="coste_total_incurrido"]': { justifyContent: 'flex-end' },
                    '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    '& .MuiDataGrid-cell--pinnedLeft': { backgroundColor: 'white', zIndex: 10 },
                    '& .MuiDataGrid-cell--pinnedLeft:not(:last-of-type)::after': {
                      content: '""', position: 'absolute', right: 0, top: 0, bottom: 0,
                      width: '1px', backgroundColor: 'rgba(224, 224, 224, 1)',
                    },
                    '& .MuiDataGrid-columnHeader--pinnedLeft': { backgroundColor: 'white !important', zIndex: 11 },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: 'white !important',
                      borderBottom: '2px solid #dee2e6 !important',
                      '& .MuiDataGrid-columnHeaderTitle': { color: 'black !important', fontWeight: 'bold !important' },
                    },
                    '& .MuiDataGrid-columnHeader': {
                      backgroundColor: 'white !important',
                      '& .MuiDataGrid-columnHeaderTitle': { color: 'black !important', fontWeight: 'bold !important' },
                    },
                    '& .MuiDataGrid-menuIcon': { color: 'black !important' },
                    '& .MuiDataGrid-sortIcon': { color: 'black !important' },
                    '& .MuiDataGrid-iconButtonContainer': { color: 'black !important' },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* ── Tab 1: Distribución ── */}
        <TabPanel value={activeTab} index={1}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>

              {/* Distribución Innocard vs Innotrans */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#495057' }}>
                  Distribución por tipo
                  {distribTipoFiltro && (
                    <Box
                      component="span"
                      onClick={() => setDistribTipoFiltro(null)}
                      sx={{ ml: 1.5, fontSize: '0.72rem', fontWeight: 400, color: '#0d6efd', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Ver todos
                    </Box>
                  )}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <Paper
                    onClick={() => setDistribTipoFiltro(distribTipoFiltro === 'innocard' ? null : 'innocard')}
                    sx={{
                      p: 2, textAlign: 'center',
                      backgroundColor: distribTipoFiltro === 'innocard' ? '#e7f1ff' : '#f8f9fa',
                      border: distribTipoFiltro === 'innocard' ? '2px solid #0d6efd' : '1px solid #dee2e6',
                      borderRadius: '8px', cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': { backgroundColor: '#e7f1ff', borderColor: '#0d6efd' },
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0d6efd' }}>
                      {innovacion.num_innocards || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6c757d' }}>Innocards</Typography>
                    <Typography variant="caption" sx={{ color: '#6c757d' }}>
                      {total > 0 ? `${Math.round(((innovacion.num_innocards || 0) / total) * 100)}%` : '—'}
                    </Typography>
                  </Paper>
                  <Paper
                    onClick={() => setDistribTipoFiltro(distribTipoFiltro === 'innotrans' ? null : 'innotrans')}
                    sx={{
                      p: 2, textAlign: 'center',
                      backgroundColor: distribTipoFiltro === 'innotrans' ? '#fff3e0' : '#f8f9fa',
                      border: distribTipoFiltro === 'innotrans' ? '2px solid #fd7e14' : '1px solid #dee2e6',
                      borderRadius: '8px', cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': { backgroundColor: '#fff3e0', borderColor: '#fd7e14' },
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#fd7e14' }}>
                      {innovacion.num_innotrans || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6c757d' }}>Innotrans</Typography>
                    <Typography variant="caption" sx={{ color: '#6c757d' }}>
                      {total > 0 ? `${Math.round(((innovacion.num_innotrans || 0) / total) * 100)}%` : '—'}
                    </Typography>
                  </Paper>
                </Box>
                {total > 0 && (
                  <Box sx={{ height: 8, borderRadius: 4, overflow: 'hidden', display: 'flex', mb: 1 }}>
                    <Box sx={{ width: `${((innovacion.num_innocards || 0) / total) * 100}%`, backgroundColor: '#0d6efd' }} />
                    <Box sx={{ flex: 1, backgroundColor: '#fd7e14' }} />
                  </Box>
                )}
              </Box>

              {/* Eficiencia de costes */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#495057' }}>
                  Eficiencia de costes
                  {distribTipoFiltro && (
                    <Box component="span" sx={{ ml: 1, fontSize: '0.72rem', fontWeight: 400, color: distribTipoFiltro === 'innocard' ? '#0d6efd' : '#fd7e14' }}>
                      ({distribTipoFiltro === 'innocard' ? 'Innocard' : 'Innotrans'})
                    </Box>
                  )}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                    <Typography variant="body2">Coste Estimado</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#6f42c1' }}>
                      {formatCurrency(distribCostesEstimado)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                    <Typography variant="body2">Coste Incurrido</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#198754' }}>
                      {formatCurrency(distribCostesIncurrido)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>% Ejecutado</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: distribPctEjecutado > 100 ? '#dc3545' : '#198754' }}>
                      {distribPctEjecutado}%
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Distribución por estado */}
              {estadosUnicos.length > 0 && (
                <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#495057' }}>
                    Distribución por estado
                    {distribTipoFiltro && (
                      <Box component="span" sx={{ ml: 1, fontSize: '0.72rem', fontWeight: 400, color: distribTipoFiltro === 'innocard' ? '#0d6efd' : '#fd7e14' }}>
                        ({distribTipoFiltro === 'innocard' ? 'Innocard' : 'Innotrans'})
                      </Box>
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {estadosUnicos.map((estado) => {
                      const count = distribIniciativas.filter((i) => i.estado === estado).length;
                      const pct = distribIniciativas.length > 0 ? Math.round((count / distribIniciativas.length) * 100) : 0;
                      return (
                        <Paper
                          key={estado}
                          sx={{
                            p: 1.5,
                            textAlign: 'center',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            minWidth: 100,
                            flex: '1 1 100px',
                            maxWidth: 160,
                          }}
                        >
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0d6efd' }}>
                            {count}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#495057', fontWeight: 600, display: 'block' }}>
                            {getEstadoDisplay(estado)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6c757d' }}>{pct}%</Typography>
                        </Paper>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </TabPanel>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: '#6c757d' }}>
            💡 Tip: Usa los filtros para segmentar por tipo (Innocard/Innotrans), estado o área de conocimiento
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Innovation;
